// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BaseTable, Loading, UnderDevelopment } from '~/components/common';
import { InformationOrderComponent } from '~/components/module/order';
// Others
import { IInvoice } from '~/utils/interface/invoice';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getOrderDetail } from '~/thunks/order/orderThunk';
import { IHeaderCellType } from '~/utils/interface/common';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import { DateFormatEnum, TypeOrderDetailEnum } from '~/utils/enum/common';
import { formatCurrencyUSD, formatDate, formatTitleCase } from '~/utils/helper/common';
import { IOrderDetailInvoice, IParamQueryOrderDetail } from '~/utils/interface/order';
// Styles, images, icons
import styles from './InvoiceOrderTab.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const columnsOrderInvoice = (t: TFunction): IHeaderCellType<IInvoice>[] => {
  return [
    {
      id: 'text',
      label: t('order_detail_title_invoice_number'),
      render: (record: IInvoice) => record.invoiceNumber,
    },
    {
      id: 'term',
      label: t('order_detail_title_term'),
      render: (record: IInvoice) => formatTitleCase(record?.paymentTerms) || EMPTY_VALUE,
    },
    {
      id: 'dueDate',
      label: t('order_detail_title_due_date'),
      render: (record: IInvoice) => formatDate(record?.dueDate, DateFormatEnum.MM_DD_YYYY) || EMPTY_VALUE,
    },
    {
      id: 'total',
      label: t('order_detail_title_total'),
      render: (record: IInvoice) => formatCurrencyUSD({ value: record?.total }) || EMPTY_VALUE,
    },
    {
      id: 'amountPaid',
      label: t('order_detail_title_amount_received'),
      render: (record: IInvoice) => formatCurrencyUSD({ value: record?.amountPaid }) || EMPTY_VALUE,
    },
    {
      id: 'balanceDue',
      label: t('order_detail_title_bal_due'),
      render: (record: IInvoice) => formatCurrencyUSD({ value: record?.balanceDue }) || EMPTY_VALUE,
    },
    // {
    //   id: 'text',
    //   label: t('order_detail_title_duration_invoice'),
    //   render: (record: IInvoice) => EMPTY_VALUE,
    // },
  ];
};

const InvoiceOrderTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { id: orderId } = useParams();
  const { t } = useTranslation(['admin/order', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detailOrder, setDetailOrder] = useState<IOrderDetailInvoice>();

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IParamQueryOrderDetail = {
      orderId: orderId || EMPTY_STRING,
      type: TypeOrderDetailEnum.INVOICE,
    };

    handleGetOrderDetail(params);
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetOrderDetail = (params: IParamQueryOrderDetail) => {
    setIsLoading(true);

    dispatch(getOrderDetail(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        setDetailOrder(data as IOrderDetailInvoice);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleProceedToSignature = () => {
    handleShowUnderDevelopment();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Stack id='invoiceOrderTabComponent' className={cx('container')} gap={8}>
      <InformationOrderComponent detailOrder={detailOrder} />

      <Stack>
        <Typography className={cx('title')}>{t('order_detail_title_invoices')}</Typography>

        <BaseTable dataSource={detailOrder?.invoice} columns={columnsOrderInvoice(t)} />
      </Stack>

      <Grid2 container className={cx('viewBox')}>
        <Grid2 size={7} />

        <Grid2 size={5}>
          <Stack gap={'15px'}>
            <Stack direction={'row'} justifyContent={'space-between'} gap={4} alignItems={'center'}>
              <Typography className={cx('label')}>{t('order_detail_title_subtotal')}</Typography>

              <Typography className={cx('value')}>{formatCurrencyUSD({ value: detailOrder?.subtotal })}</Typography>
            </Stack>

            <Stack direction={'row'} justifyContent={'space-between'} gap={4} alignItems={'center'}>
              <Typography className={cx('label')}>{t('order_detail_title_rental_tax_sub')}</Typography>

              <Typography className={cx('value')}>{formatCurrencyUSD({ value: '' })}</Typography>
            </Stack>

            <Stack direction={'row'} justifyContent={'space-between'} gap={4} alignItems={'center'}>
              <Typography className={cx('label')}>
                {t('order_detail_title_tax', { value: detailOrder?.taxRate })}
              </Typography>

              <Typography className={cx('value')}>{formatCurrencyUSD({ value: detailOrder?.taxAmount })}</Typography>
            </Stack>

            <Stack direction={'row'} justifyContent={'space-between'} gap={4} alignItems={'center'}>
              <Typography className={cx('label')}>{t('order_detail_title_discount')}</Typography>

              <Typography className={cx('value')}>{formatCurrencyUSD({ value: '' })}</Typography>
            </Stack>

            <Box className={cx('line')} />

            <Stack direction={'row'} justifyContent={'space-between'} gap={4} alignItems={'center'}>
              <Typography className={cx('labelTotal')}>{t('order_detail_title_total_invoice')}</Typography>

              <Typography className={cx('valueTotal')}>{formatCurrencyUSD({ value: detailOrder?.total })}</Typography>
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>

      {/*<Stack alignItems={'center'} justifyContent={'center'}>*/}
      {/*  <Box>*/}
      {/*    <Button variant='contained' className={cx('buttonProceed')} onClick={handleProceedToSignature}>*/}
      {/*      {t('order_detail_proceed_to_signature')}*/}
      {/*    </Button>*/}
      {/*  </Box>*/}
      {/*</Stack>*/}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Stack>
  );
};

export default InvoiceOrderTab;
